import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BATTLE_TOOLTIP_ANCHOR_GAP_PX,
  BATTLE_TOOLTIP_VIEWPORT_PAD_PX,
  computeBattleTooltipPlacement,
} from '../../utils/battleTooltipViewportPlacement';
import './MobileTooltipLayer.css';

/**
 * Unified mobile tooltip layer.
 *
 * On mobile the per-element CSS `[data-tooltip]::after` tooltips are unreliable:
 *  - When a `[data-tooltip]` element lives inside a transform-scaled HUD (opponent
 *    HUD ≈ 0.405, player HUD ≈ 0.54) the pseudo-element text scales down with it
 *    and becomes unreadable ("too small").
 *  - They open straight up/down relative to a tiny element at a screen edge with no
 *    viewport clamping, so they spill off the top/bottom/sides ("outside the screen").
 *
 * This component routes every `[data-tooltip]` hover/tap through a single portaled
 * tooltip rendered to `document.body`. Because it is portaled and fixed-positioned
 * it escapes all HUD transforms, renders at one consistent readable size, and is
 * clamped to the viewport (auto-flipping above/below the anchor as needed).
 *
 * While active it adds `coda-mobile-tooltip-mode` to <body> so the native
 * `::after`/`::before` tooltips are suppressed (see BattleUI.css) and we never
 * render two tooltips at once.
 */

const BODY_MODE_CLASS = 'coda-mobile-tooltip-mode';

function readTooltipTarget(node) {
  if (!node || typeof node.closest !== 'function') return null;
  const el = node.closest('[data-tooltip]');
  if (!el) return null;
  const text = el.getAttribute('data-tooltip');
  if (text == null || text === '') return null;
  return el;
}

export default function MobileTooltipLayer({ active }) {
  const [tooltip, setTooltip] = useState(null);
  const [placement, setPlacement] = useState({ left: 0, top: 0, ready: false });
  const tooltipRef = useRef(null);
  const anchorRef = useRef(null);

  const clearTooltip = useCallback(() => {
    anchorRef.current = null;
    setTooltip(null);
    setPlacement({ left: 0, top: 0, ready: false });
  }, []);

  const showForElement = useCallback((el) => {
    if (!el) return;
    const text = el.getAttribute('data-tooltip');
    if (text == null || text === '') return;
    anchorRef.current = el;
    setPlacement({ left: 0, top: 0, ready: false });
    setTooltip({
      text: String(text),
      preferBottom: el.getAttribute('data-tooltip-position') === 'bottom',
    });
  }, []);

  // Reposition once the tooltip has been measured (and on scroll/resize).
  useLayoutEffect(() => {
    if (!tooltip) return undefined;

    const reposition = () => {
      const anchor = anchorRef.current;
      const tip = tooltipRef.current;
      if (!anchor || !tip) return;

      const rect = anchor.getBoundingClientRect();
      const tipWidth = tip.offsetWidth;
      const tipHeight = tip.offsetHeight;
      const { left, top } = computeBattleTooltipPlacement(rect, tipWidth, tipHeight, {
        preferBottom: tooltip.preferBottom,
        pad: BATTLE_TOOLTIP_VIEWPORT_PAD_PX,
        gap: BATTLE_TOOLTIP_ANCHOR_GAP_PX,
      });

      setPlacement({ left, top, ready: true });
    };

    const raf = requestAnimationFrame(reposition);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [tooltip]);

  // Event delegation for hover (desktop/devtools) and tap (touch).
  useEffect(() => {
    if (!active) return undefined;

    document.body.classList.add(BODY_MODE_CLASS);

    // Hover (mouse / devtools mouse emulation): show on enter, hide on leave.
    const handlePointerOver = (event) => {
      if (event.pointerType !== 'mouse') return;
      const el = readTooltipTarget(event.target);
      if (el) showForElement(el);
      else if (anchorRef.current && !anchorRef.current.contains(event.target)) clearTooltip();
    };

    const handlePointerOut = (event) => {
      if (event.pointerType !== 'mouse' || !anchorRef.current) return;
      const related = event.relatedTarget;
      if (related && anchorRef.current.contains(related)) return;
      clearTooltip();
    };

    // Tap (touch / pen): toggle on the element; tapping elsewhere dismisses. For
    // mouse, clicks don't dismiss (hover owns visibility), they just keep it shown.
    const handlePointerDown = (event) => {
      const el = readTooltipTarget(event.target);
      if (el) {
        if (event.pointerType === 'mouse') return;
        if (anchorRef.current === el) clearTooltip();
        else showForElement(el);
        return;
      }
      if (anchorRef.current) clearTooltip();
    };

    document.addEventListener('pointerover', handlePointerOver, true);
    document.addEventListener('pointerout', handlePointerOut, true);
    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      document.body.classList.remove(BODY_MODE_CLASS);
      document.removeEventListener('pointerover', handlePointerOver, true);
      document.removeEventListener('pointerout', handlePointerOut, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
      anchorRef.current = null;
    };
  }, [active, showForElement, clearTooltip]);

  // Drop any open tooltip when the layer deactivates.
  useEffect(() => {
    if (!active && tooltip) clearTooltip();
  }, [active, tooltip, clearTooltip]);

  if (!active || !tooltip || typeof document === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className="mobile-tooltip-layer"
      role="tooltip"
      style={{
        left: `${placement.left}px`,
        top: `${placement.top}px`,
        opacity: placement.ready ? 1 : 0,
      }}
    >
      {tooltip.text}
    </div>,
    document.body
  );
}
